import React from "react";
import styled from "styled-components";
import { Row, Col } from "react-bootstrap";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";
import ReactMarkdown from "react-markdown";
import useResponsiveImageUrl from "src/utils/useResponsiveImageUrl";
import TitleSection from "src/components/TitleSection/TitleSection";

/**
 * Dual-panel section with a shared phone image overlapping both panels.
 * Top panel: dark bg with title + content (phone on right)
 * Bottom panel: light bg with heading row (title + logo inline) + content
 */
const TournamentAppPhonePanels = ({
  topPanel,
  bottomPanel,
  image,
  imageAlt,
}) => {
  if (!topPanel) return null;

  const rawTopBgImage = useResponsiveImageUrl(topPanel.bgImage || "");
  const rawBottomBgImage = useResponsiveImageUrl(bottomPanel?.bgImage || "");
  const topBgImage = topPanel.bgImage ? rawTopBgImage : null;
  const bottomBgImage = bottomPanel?.bgImage ? rawBottomBgImage : null;

  return (
    <Wrapper>
      <TopPanel
        bgColor={topPanel.bgColor}
        bgImage={topBgImage}
        overlayColor={topPanel.overlayColor}
      >
        <TopPanelInner>
          <Row className="align-items-center h-100">
            <Col lg={7} md={12} className="h-100">
              <TopContent>
                {topPanel.title && (
                  <TitleWrap titleColor={topPanel.titleColor}>
                    <TitleSection
                      className="panel-title"
                      dark={false}
                      as="h3"
                      title={topPanel.title}
                      text={topPanel.content || ""}
                      borderColor={topPanel.lineColor || "#d61f1f"}
                    />
                  </TitleWrap>
                )}
              </TopContent>
            </Col>
            <Col lg={5} md={12} />
          </Row>
        </TopPanelInner>
      </TopPanel>

      {bottomPanel && (
        <BottomPanel
          bgColor={bottomPanel.bgColor}
          bgImage={bottomBgImage}
          overlayColor={bottomPanel.overlayColor}
        >
          <BottomPanelInner>
            <BottomContent>
              {(bottomPanel.title || bottomPanel.logo) && (
                <BottomHeading titleColor={bottomPanel.titleColor}>
                  <HeadingRow>
                    {bottomPanel.title && (
                      <HeadingText>
                        <ReactMarkdown>{bottomPanel.title}</ReactMarkdown>
                      </HeadingText>
                    )}
                    {bottomPanel.logo && (
                      <HeadingLogo>
                        <ResponsiveImage
                          src={bottomPanel.logo}
                          alt={bottomPanel.logoAlt || ""}
                        />
                      </HeadingLogo>
                    )}
                  </HeadingRow>
                  <Underline />
                </BottomHeading>
              )}
              {bottomPanel.content && (
                <BottomText
                  textColor={bottomPanel.textColor}
                  linkColor={bottomPanel.linkColor}
                >
                  <ReactMarkdown>{bottomPanel.content}</ReactMarkdown>
                </BottomText>
              )}
            </BottomContent>
          </BottomPanelInner>
        </BottomPanel>
      )}

      {image && (
        <PhoneImage>
          <ResponsiveImage src={image} alt={imageAlt || "App Mockup"} />
        </PhoneImage>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  max-width: 1280px;
  width: 100%;
  border-radius: 20px;
  .title-section {
    align-items: flex-start;
    p {
      margin-bottom: 0;
      font-size: 1.2rem;
      text-align: left !important;
    }
    p:first-of-type {
      margin-top: 0;
    }
  }

  @media (max-width: 768px) {
    .title-section p {
      font-size: inherit;
    }
  }
`;

const PanelBase = styled.div`
  background: ${(props) => props.bgColor || "#1a1a2e"};
  ${(props) =>
    props.bgImage &&
    `
    background-image: url(${props.bgImage});
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  `}
  position: relative;

  &:before {
    content: "";
    position: absolute;
    inset: 0;
    background: ${(props) => props.overlayColor || "transparent"};
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const TopPanel = styled(PanelBase)`
  width: 100vw;
  margin-left: calc(50% - 50vw);
  border-radius: 0;
  min-height: 360px;

  &:before {
    border-radius: 0;
  }

  @media (max-width: 768px) {
    min-height: auto;
  }
`;

const TopPanelInner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 2rem;
  min-height: 360px;
  height: 100%;

  @media (max-width: 768px) {
    min-height: auto;
    padding: 2rem 1.5rem;
  }
`;

const TopContent = styled.div`
  padding: 2.5rem 2.25rem 2.5rem 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const TitleWrap = styled.div`
  .panel-title {
    padding: 0;
    align-items: flex-start;
    text-align: left;
    width: 100%;
    color: ${(props) => props.titleColor || "#fff"};
  }

  .panel-title > h3 {
    font-size: 32px;
  }

  .panel-title > span {
    margin-bottom: 0.65rem;
    max-width: 165px;
  }

  .panel-title > p {
    display: block;
  }
`;

const BottomPanel = styled(PanelBase)`
  width: 100vw;
  margin-left: calc(50% - 50vw);
  border-radius: 0;
  min-height: 250px;

  &:before {
    border-radius: 0;
  }
`;

const BottomPanelInner = styled.div`
  max-width: 1180px;
  margin: 0 auto;
`;

const BottomContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 2rem 2.35rem;
  max-width: 65%;

  @media (max-width: 768px) {
    max-width: 100%;
    padding: 1.5rem;
  }
`;

const BottomHeading = styled.div`
  color: ${(props) => props.titleColor || "#111"};
  margin-bottom: 0.85rem;
`;

const HeadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
`;

const HeadingText = styled.div`
  h2,
  h3,
  h4 {
    margin: 0;
    color: inherit;
    font-weight: 700;
    line-height: 1.2;
  }
`;

const HeadingLogo = styled.div`
  display: inline-flex;
  align-items: center;

  img {
    max-width: 150px;
    margin-bottom: 5px;
    width: 100%;
    height: auto;
  }
`;

const Underline = styled.div`
  width: 165px;
  border-bottom: 3px solid #d61f1f;
  margin-top: 0.5rem;
`;

const BottomText = styled.div`
  color: ${(props) => props.textColor || "#222"};
  font-size: 1.02rem;
  line-height: 1.48;

  p {
    margin: 0 0 0.45rem;
    font-size: 1.2rem;
    color: inherit;
  }

  @media (max-width: 768px) {
    p {
      font-size: inherit;
    }
  }

  strong {
    color: inherit;
    font-weight: 700;
  }

  a {
    color: ${(props) => props.linkColor || "#cc0000"};
    font-weight: 700;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const PhoneImage = styled.div`
  position: absolute;
  right: 1.6rem;
  top: 0;
  bottom: -10%;
  margin: auto 0;
  width: 45%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  pointer-events: none;
  z-index: 3;
  height: 100%;
  max-height: 550px;
  img {
    position: relative;
    width: 100%;
    max-width: 600px;
    height: auto;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    position: relative;
    width: 100%;
    right: 0;
    top: 0;
    bottom: auto;
    margin: 0;
    transform: none;
    justify-content: center;
    margin-top: 1rem;
    pointer-events: auto;

    img {
      max-width: 360px;
    }
  }
`;

export default TournamentAppPhonePanels;
